import { Button } from "@mui/material"
import { useDialog } from "lib/@hooks/useDialog"
import { isLockedToLegacy, ProjectPhase } from "authoring/lib/enum/project-phase.mjs"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { getLegacyScheduleSplit, isEligibleForLegacySend } from "../lib/get-legacy-schedule-split"
import { sendProjectToLegacy } from "../controller/send-project-to-legacy"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { SentToLegacyDialog } from "authoring/routes/project/components/sent-to-legacy-dialog"
import { SendToLegacyBlockersDialog } from "../components/send-to-legacy-blockers-dialog"
import { SendToLegacyOptionsDialog } from "../components/send-to-legacy-options-dialog"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { AUTHORING_PUBLISH_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function PublishToLegacyButton() {
    const { target: project, resolvedSchedules = [] } = useBoundContext()
    const scheduleIds = project?.scopedScheduleIds ?? []
    const approvedSchedules = resolvedSchedules.filter(isEligibleForLegacySend)

    const locked = isLockedToLegacy(project?.phase)
    const noApprovedSchedules = !approvedSchedules.length
    const buttonVariant = locked ? "outlined" : "contained"
    const buttonLabel = locked ? "Published to Legacy" : "Publish"

    const sendToLegacyBlockersDialog = useDialog(
        <SendToLegacyBlockersDialog
            actionLabel="Publish approved schedules"
            actionVerb="published"
            noApprovedTitle="No approved drafts to publish"
            emptyMessage="Add at least one schedule before publishing this work package to Legacy."
            blockersMessage="The following drafts are not approved and will not be published."
        />,
        { maxWidth: "md" }
    )
    const sendToLegacyOptionsDialog = useDialog(
        <SendToLegacyOptionsDialog actionLabel="Publish" actionVerb="published" showIncrementVersion={false} />,
        { maxWidth: "md" }
    )
    const sentToLegacyDialog = useDialog(
        <SentToLegacyDialog
            title="Updates published to Legacy"
            intro="You published all changes to Legacy. This means the work package is locked and you can't make any further edits here."
            summary="This work package is now published"
        />
    )

    if (project?.phase === ProjectPhase.PUSHED_TO_LEGACY) return null
    if (project?.archived) return null
    return (
        <Secure demand={AUTHORING_PUBLISH_DEMAND} fallback={<Frag />}>
            <Button
                data-cy="publish-button"
                aria-locked={locked}
                startIcon={locked && <Iconify icon={"material-symbols:lock"} />}
                variant={buttonVariant}
                disabled={noApprovedSchedules && !locked}
                onClick={handleClick}
            >
                {buttonLabel}
            </Button>
        </Secure>
    )

    async function handleClick() {
        if (locked) {
            await sentToLegacyDialog()
            return
        }

        const split = await getLegacyScheduleSplit({ projectId: project?.id, scheduleIds })
        if (!split) {
            errorSnackbar("Could not check the schedules in this work package. Please try again.")
            return
        }

        const { approved, blocking, alreadyPublished } = split

        if (!approved.length || blocking.length || alreadyPublished.length) {
            const confirmed = await sendToLegacyBlockersDialog({
                blockingSchedules: blocking,
                alreadyPublishedSchedules: alreadyPublished,
                scheduleCount: scheduleIds.length,
                approvedCount: approved.length,
            })
            if (!confirmed) return
        }

        const options = await sendToLegacyOptionsDialog({ approvedSchedules: approved })
        if (!options) return

        const result = await sendProjectToLegacy({
            projectId: project?.id,
            incrementVersion: options.incrementVersion,
            publish: true,
        })
        if (!result?.success) {
            errorSnackbar(result?.error || "Publish failed")
            return
        }

        successSnackbar("Approved schedules published to legacy successfully")
        for (const warning of result?.warnings ?? []) {
            warningSnackbar(warning)
        }
    }
}
